import {
  flowRight as compose,
  groupBy as gb,
  map,
  filter,
  flatMap,
  property,
  curryRight,
} from 'lodash-es';
import { asyncFallback } from '@mummssoftware/utils/utils';
import { notesHavePSO } from '../../../utils';
import {
  patientNumbersForPhysician,
  physicianIdSet,
} from './physicianPickerUtils';

/**
 * @typedef {mumms.Approval} Approval
 * @typedef {mumms.patient} PatientHeader
 * @typedef {import('@mummssoftware/utils/api/Pim').Pim} PimApi
 * @typedef {import('@mummssoftware/utils/api/Workflow').Workflow} WorkflowApi
 * @typedef {import('@mummssoftware/utils/api/Observation').Observation} ObservationApi
 *
 * @typedef EnrichDeps
 * @prop {ObservationApi} observation
 * @prop {PimApi} pim
 * @prop {string} hospiceBin
 * @prop {string} [agency]
 *
 * @typedef {Approval & { note?: object, pso?: object | null }} EnrichedApproval
 *
 * @typedef PatientGroupPayload
 * @prop {EnrichedApproval[]} printApprovals
 * @prop {PatientHeader} patientInfo
 *
 * @typedef {EnrichedApproval & { patientInfo: PatientHeader }} UnsignedVerbalRow
 */

const cFilter = curryRight(filter);
const cFlatMap = curryRight(flatMap);

const getPhysicianPersonId = property('physicianpersonid');
const getPhysicianPersonNumber = property('physicianpersonnumber');
const getPatientNumber = property('patientnumber');
const getEntityId = property('entityid');
const getApprovalId = property('id');

const strEq = (a) => (b) => String(b) === String(a).trim();

const matchesPhysician = (key) => (approval) =>
  strEq(key)(getPhysicianPersonId(approval)) ||
  strEq(key)(getPhysicianPersonNumber(approval));

const matchesPatient = (pn) => compose(strEq(pn), getPatientNumber);

const groupByPatient = compose(Object.entries, (approvals) =>
  gb(approvals, getPatientNumber),
);

const filterToPatient = (pn, physicianKey) =>
  cFilter(
    (approval) =>
      matchesPatient(pn)(approval) && matchesPhysician(physicianKey)(approval),
  );

/**
 * Fan out a `PatientGroupPayload[]` into `UnsignedVerbalRow[]`, stamping
 * `patientInfo` onto each enriched approval.
 * @type {(payloads: PatientGroupPayload[]) => UnsignedVerbalRow[]}
 */
const flattenPayloads = cFlatMap(({ printApprovals, patientInfo }) =>
  map(printApprovals, (dto) => ({ ...dto, patientInfo })),
);

/**
 * @param {Approval} approval
 * @param {ObservationApi} observation
 * @param {string} hospiceBin
 * @returns {Promise<EnrichedApproval>}
 */
async function enrichOneApproval(approval, observation, hospiceBin) {
  try {
    const raw = await observation.getProgressNotesById(
      String(getEntityId(approval)),
      hospiceBin,
    );
    const note = Array.isArray(raw) ? raw[0] : raw;
    if (!note || (note.status && note.status === 404)) {
      return { ...approval };
    }
    const psoEntityKey = notesHavePSO(approval, note);
    const pso = psoEntityKey
      ? (await observation.getPatientStandingOrderByIdV2(
          psoEntityKey,
          hospiceBin,
        )) || null
      : undefined;
    return pso != null ? { ...approval, note, pso } : { ...approval, note };
  } catch (_) {
    return { ...approval };
  }
}

/**
 * @param {[string, Approval[]]} group `[patientNumber, approvals]`
 * @param {EnrichDeps} deps
 * @returns {Promise<PatientGroupPayload>}
 */
async function enrichPatientGroup([patientNumber, approvals], deps) {
  const { observation, pim, hospiceBin, agency } = deps;
  const [enrichedApprovals, patientInfo] = await Promise.all([
    Promise.all(
      map(approvals, (approval) =>
        enrichOneApproval(approval, observation, hospiceBin),
      ),
    ),
    pim.getPatientHeader(patientNumber, agency),
  ]);
  return { printApprovals: enrichedApprovals, patientInfo };
}

/**
 * Single-patient unsigned verbals for print: physician VA list (one request),
 * narrowed to this patient, then progress notes / PSO / patient header.
 *
 * @param {object} args
 * @param {string} args.patientNumber
 * @param {string|number} args.physicianPersonId
 * @param {string|number} [args.approvalId] OP-31894: narrow to this one order
 * @param {WorkflowApi} args.workflow
 * @param {ObservationApi} args.observation
 * @param {PimApi} args.pim
 * @param {string} args.hospiceBin
 * @param {string} [args.agency]
 * @param {string|number} args.siteId
 * @returns {Promise<PatientGroupPayload>}
 */
export async function loadPatientVerbalApprovalsForPrint({
  patientNumber,
  physicianPersonId,
  approvalId,
  workflow,
  observation,
  pim,
  hospiceBin,
  agency,
  siteId,
}) {
  const physicianKey = String(physicianPersonId).trim();
  const workflowResponse = await workflow.getPhysicianVerbalApprovals(
    physicianKey,
    hospiceBin,
    siteId,
  );

  const vaList = Array.isArray(workflowResponse) ? workflowResponse : [];
  const patientWorkflow = filterToPatient(patientNumber, physicianKey)(vaList);
  const filteredWorkflow =
    approvalId === undefined || approvalId === null
      ? patientWorkflow
      : filter(patientWorkflow, (approval) =>
          strEq(approvalId)(getApprovalId(approval)),
        );

  return enrichPatientGroup([patientNumber, filteredWorkflow], {
    observation,
    pim,
    hospiceBin,
    agency,
  });
}

/**
 * Bulk: per-patient enrichment only.
 * When `workFlowApprovals` (from ApprovalContext) is supplied the VA list
 * already fetched for the grid is reused — no extra network call.
 * Falls back to getPhysicianVerbalApprovals when it is absent.
 *
 * @param {object} args
 * @param {string|number} args.physicianPersonId
 * @param {Array<object>} [args.compiledApprovals] rows from the listing grid
 * @param {Approval[]} [args.workFlowApprovals] cached VA list from ApprovalContext
 * @param {WorkflowApi} args.workflow
 * @param {ObservationApi} args.observation
 * @param {PimApi} args.pim
 * @param {string} args.hospiceBin
 * @param {string} [args.agency]
 * @param {string|number} args.siteId
 * @returns {Promise<UnsignedVerbalRow[]>}
 */
export async function loadBulkUnsignedVerbalsForPrint({
  physicianPersonId,
  compiledApprovals,
  workFlowApprovals,
  workflow,
  observation,
  pim,
  hospiceBin,
  agency,
  siteId,
}) {
  const physicianKey = String(physicianPersonId).trim();

  const patientNumbers = patientNumbersForPhysician(
    compiledApprovals,
    physicianPersonId,
  );
  if (!patientNumbers.length) return [];

  const cachedList =
    Array.isArray(workFlowApprovals) && workFlowApprovals.length
      ? workFlowApprovals
      : null;

  const response = await asyncFallback(cachedList, () =>
    workflow.getPhysicianVerbalApprovals(physicianKey, hospiceBin, siteId),
  );
  const vaList = Array.isArray(response) ? response : [];

  // Build a set of every raw ID form (personid + personnumber) that refers to
  // this physician. Needed because the canonical key may have been resolved via
  // the orderingToPersonId map, and some raw approvals only carry the
  // person-number form (physicianpersonid = null).
  const physicianIds = physicianIdSet(compiledApprovals, physicianKey);
  const matchesPhysicianIds = (approval) =>
    physicianIds.has(String(getPhysicianPersonId(approval))) ||
    physicianIds.has(String(getPhysicianPersonNumber(approval)));

  const approvalsForPhysician = filter(vaList, matchesPhysicianIds);
  if (!approvalsForPhysician.length) return [];

  const patientGroups = groupByPatient(approvalsForPhysician);
  const deps = { observation, pim, hospiceBin, agency };

  const payloads = await Promise.all(
    map(patientGroups, (group) => enrichPatientGroup(group, deps)),
  );

  return flattenPayloads(payloads);
}
